import React, { useState } from 'react';
import { supabase } from '../services/supabaseService';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Home as RoomsIcon,
  AttachMoney as PriceIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';

const TenantProfileCarousel = ({ title = "Ostatnio oglądani", profiles, onTenantClick, userId, onFavoritesChange, favoriteIds: externalFavoriteIds, onLoginRequired, onAddListing, listMode = false }) => {
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [favorites, setFavorites] = useState(new Set());
  const [initialLoaded, setInitialLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Synchronizuj z zewnętrznymi favoriteIds (z App.js)
  React.useEffect(() => {
    if (externalFavoriteIds !== undefined && initialLoaded) {
      setFavorites(new Set(externalFavoriteIds));
    }
  }, [externalFavoriteIds, initialLoaded]);

  // Załaduj ulubione z Supabase przy mountowaniu
  React.useEffect(() => {
    if (!userId) {
      setFavorites(new Set());
      setInitialLoaded(true);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('users')
        .select('favorite_tenants')
        .eq('id', userId)
        .single();
      if (!error && data && data.favorite_tenants && Array.isArray(data.favorite_tenants)) {
        setFavorites(new Set(data.favorite_tenants));
      }
      setInitialLoaded(true);
    })();
  }, [userId]);
  
  // Funkcja do zamiany pierwszej litery na wielką
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Wykrywanie żeńskiego imienia (polskie imiona żeńskie kończą się na -a, z wyjątkami)
  const isFemale = (name) => {
    if (!name) return false;
    const first = name.trim().split(/\s+/)[0].toLowerCase();
    const maleExceptions = ['kuba', 'barnaba', 'kosma', 'bonawentura', 'jarema', 'sasza'];
    if (maleExceptions.includes(first)) return false;
    return first.endsWith('a');
  };

  // Formatowanie wieku — wartość z bazy to liczba (18, 20, 30...), wyświetlamy jako 'Wiek: 20+'
  const formatAge = (age) => {
    const str = String(age);
    if (str.includes('+')) return `Wiek: ${str}`;
    const n = Number(age);
    if (!n || isNaN(n)) return `Wiek: ${str}`;
    return `Wiek: ${n}+`;
  };
  
  const itemsPerView = isMobile ? 1 : 3;
  
  // Early return if no profiles
  if (!profiles || profiles.length === 0) {
    if (listMode) {
      return (
        <Box sx={{ textAlign: 'center', py: 6, width: '100%' }}>
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 600 }}>
            Brak najemców o podanych kryteriach
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
            Zmień ustawienia filtra
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ textAlign: 'center', py: 8, px: 2, width: '100%' }}>
        <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>👤</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
          Brak najemców
        </Typography>
        <Typography variant="body1" sx={{ color: '#777', mb: 3 }}>
          Nie znaleźliśmy profili pasujących do kryteriów.<br/>Spróbuj zmienić filtry lub dodaj swoje ogłoszenie.<br/>Ustaw filtry, aby opisać swoją nieruchomość.
        </Typography>
        {onAddListing && (
          <Button
            variant="contained"
            size="large"
            onClick={onAddListing}
            sx={{
              backgroundColor: '#D2691E',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 700,
              '&:hover': { backgroundColor: '#B85C00' }
            }}
          >
            + Dodaj ogłoszenie
          </Button>
        )}
      </Box>
    );
  }
  
  const maxIndex = Math.max(0, profiles.length - itemsPerView);
  const cardWidth = isMobile ? 100 : 320 + 16; // 320px + 16px gap

  const nextSlide = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  const toggleFavorite = (profileId) => {
    if (!userId) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(profileId)) {
        newFavorites.delete(profileId);
      } else {
        newFavorites.add(profileId);
      }
      // Zapisz do Supabase
      const favArray = Array.from(newFavorites);
      supabase
        .from('users')
        .update({ favorite_tenants: favArray })
        .eq('id', userId)
        .select('id')
        .then((result) => {
          if (result.error) {
          } else if (result.data && result.data.length > 0) {
            if (onFavoritesChange) onFavoritesChange();
          } else {
          }
        });
      return newFavorites;
    });
  };

  // Touch/swipe handlers for mobile
  let touchStartX = null;
  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchEndX - touchStartX;
    if (diff > 50 && currentIndex > 0) {
      prevSlide();
    } else if (diff < -50 && currentIndex < maxIndex) {
      nextSlide();
    }
    touchStartX = null;
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <Chip
        label="Zweryfikowany"
        size="small"
        sx={{
          backgroundColor: '#22C55E',
          color: 'white',
          fontSize: '0.7rem',
          height: '20px'
        }}
      />
    ) : null;
  };

  // LIST MODE – PropertyCard-style layout (used for search results)
  if (listMode) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2D3748' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {profiles.map((profile, index) => (
            <Box
              key={profile.id || index}
              onClick={() => onTenantClick && onTenantClick(profile)}
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'stretch',
                mb: 2,
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'box-shadow 0.3s ease',
                '&:hover': { boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }
              }}
            >
              {/* Lewa kolumna – okrągły avatar jak w PropertyCard */}
              <Box sx={{
                width: isMobile ? '100%' : 160,
                minWidth: isMobile ? 'auto' : 160,
                height: isMobile ? 100 : 'auto',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F7FAFC',
                borderRight: isMobile ? 'none' : '1px solid #f0f0f0',
                borderBottom: isMobile ? '1px solid #f0f0f0' : 'none',
                p: 2,
              }}>
                <Avatar
                  src={profile.avatar}
                  alt={profile.name}
                  sx={{
                    width: 90,
                    height: 90,
                    border: '3px solid #D2691E',
                    backgroundColor: '#ffe8d6',
                  }}
                >
                  <PersonIcon sx={{ color: '#D2691E', fontSize: 48 }} />
                </Avatar>
              </Box>

              {/* Prawa kolumna – treść */}
              <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
                {/* Przycisk ulubieni */}
                <IconButton
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(profile.id); }}
                  sx={{
                    position: 'absolute', top: 8, right: 8,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    width: 32, height: 32,
                    '&:hover': { backgroundColor: 'white', transform: 'scale(1.1)' },
                    transition: 'all 0.2s ease', zIndex: 2
                  }}
                >
                  {favorites.has(profile.id)
                    ? <FavoriteIcon sx={{ color: '#D2691E', fontSize: 18 }} />
                    : <FavoriteBorderIcon sx={{ color: '#666', fontSize: 18 }} />}
                </IconButton>

                {/* Imię + weryfikacja */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {profile.name || 'Brak imienia'}
                  </Typography>
                  {getVerificationBadge(profile.isVerified)}
                </Box>

                {/* Wiek + zawód */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  {profile.age && <Chip label={formatAge(profile.age)} size="small" sx={{ backgroundColor: '#E2E8F0', color: '#4A5568', fontSize: '0.75rem', height: '22px' }} />}
                  {(profile.profession || profile.occupation) && <Chip label={profile.profession || profile.occupation} size="small" sx={{ backgroundColor: '#F0F4F8', color: '#2D3748', fontSize: '0.75rem', height: '22px' }} />}
                </Box>

                {/* Miasto */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationIcon sx={{ fontSize: '16px', mr: 0.5, color: '#EE2005' }} />
                  <Typography variant="body2" color="text.secondary">{profile.city}</Typography>
                </Box>

                {/* Pokoje i budżet */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RoomsIcon sx={{ color: '#D2691E', fontSize: '18px' }} />
                    <Typography variant="body2"><strong>Pokoje:</strong> {profile.roomsWanted != null && profile.roomsWanted !== '' ? (Number(profile.roomsWanted) === 0 ? 'Pokój w mieszkaniu' : Number(profile.roomsWanted) === 1 ? 'Kawalerka' : profile.roomsWanted) : 'Nie określono'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PriceIcon sx={{ color: '#D2691E', fontSize: '18px' }} />
                    <Typography variant="body2"><strong>Budżet:</strong> {profile.priceRange || 'Nie określono'}</Typography>
                  </Box>
                  {profile.moveInDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon sx={{ color: '#D2691E', fontSize: '18px' }} />
                      <Typography variant="body2"><strong>Od:</strong> {new Date(profile.moveInDate).toLocaleDateString('pl-PL')}</Typography>
                    </Box>
                  )}
                </Box>

                {/* Opis */}
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {profile.description
                    ? (profile.description.length > 50 ? profile.description.substring(0, 50) + '...' : profile.description)
                    : ''}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 6 }}>
      {/* Title */}
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 3, 
          fontWeight: 'bold',
          color: '#2D3748'
        }}
      >
        {title}
      </Typography>

      {/* Carousel Container */}
      <Box sx={{ 
        position: 'relative',
        overflow: 'hidden',
        width: '100%'
      }}>
        {/* Navigation Arrows */}
        {!isMobile && (
          <>
            <IconButton
              onClick={prevSlide}
              disabled={currentIndex === 0}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                },
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={nextSlide}
              disabled={currentIndex === maxIndex}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                },
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}

        {/* Cards Container */}
        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            display: 'flex',
            gap: { xs: 0, sm: 2 },
            transform: `translateX(-${currentIndex * (isMobile ? 100 : cardWidth)}${isMobile ? '%' : 'px'})`,
            transition: 'transform 0.3s ease-in-out',
            width: 'auto'
          }}
        >
          {profiles.map((profile, index) => {
            if (index === 0) {
            }
            return (
            <Card
              key={index}
              onClick={() => onTenantClick && onTenantClick(profile)}
              sx={{
                width: isMobile ? '100%' : '320px',
                minWidth: isMobile ? '100%' : '320px',
                maxWidth: isMobile ? '100%' : '320px',
                flexShrink: 0,
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative' }}>
                {/* Favorite Button */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(profile.id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease',
                    zIndex: 2
                  }}
                >
                  {favorites.has(profile.id) ? (
                    <FavoriteIcon sx={{ color: '#D2691E' }} />
                  ) : (
                    <FavoriteBorderIcon sx={{ color: '#666' }} />
                  )}
                </IconButton>

                {/* Profile Header */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2 
                }}>
                  <Avatar
                    src={profile.avatar}
                    alt={profile.name}
                    sx={{
                      width: 60,
                      height: 60,
                      mr: 2,
                      border: '3px solid #D2691E',
                      backgroundColor: '#F7FAFC'
                    }}
                  >
                    <PersonIcon sx={{ color: '#D2691E' }} />
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mb: 0.5 
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }}
                      >
                        {profile.name || 'Brak imienia'}
                      </Typography>
                      {getVerificationBadge(profile.isVerified)}
                    </Box>
                    
                    {/* Wiek, zawód i status palenia */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                      flexWrap: 'wrap'
                    }}>
                      {profile.age && (
                        <Chip 
                          label={formatAge(profile.age)}
                          size="small"
                          sx={{ 
                            backgroundColor: '#E2E8F0', 
                            color: '#4A5568',
                            fontSize: '0.75rem',
                            height: '22px'
                          }}
                        />
                      )}
                      {(profile.profession || profile.occupation) && (
                        <Chip 
                          label={capitalize(profile.profession || profile.occupation)}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F0F4F8', 
                            color: '#2D3748',
                            fontSize: '0.75rem',
                            height: '22px'
                          }}
                        />
                      )}
                      {(profile.smoking === false || profile.smoker === false) && (
                        <Chip 
                          label={isFemale(profile.name) ? 'Niepaląca' : 'Niepalący'}
                          size="small"
                          sx={{ 
                            backgroundColor: '#F0FFF4', 
                            color: '#22543D',
                            fontSize: '0.75rem',
                            height: '22px'
                          }}
                        />
                      )}
                      {profile.petOwner && (
                        <Chip 
                          label="Zwierzęta"
                          size="small"
                          sx={{ 
                            backgroundColor: '#FFF7ED', 
                            color: '#C05621',
                            fontSize: '0.75rem',
                            height: '22px'
                          }}
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'text.secondary' 
                    }}>
                      <LocationIcon sx={{ fontSize: '16px', mr: 0.5, color: '#EE2005' }} />
                      <Typography variant="body2">
                        {profile.city}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Profile Details */}
                <Box sx={{ mb: 2 }}>
                  {/* Looking for details - Pokoje i Budżet */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1.5,
                    mb: 2
                  }}>
                    {/* Rooms */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <RoomsIcon sx={{ 
                        color: '#D2691E', 
                        fontSize: '18px' 
                      }} />
                      <Typography variant="body2">
                        <strong>Pokoje:</strong> {profile.roomsWanted != null && profile.roomsWanted !== '' ? (Number(profile.roomsWanted) === 0 ? 'Pokój w mieszkaniu' : Number(profile.roomsWanted) === 1 ? 'Kawalerka' : profile.roomsWanted) : 'Nie określono'}
                      </Typography>
                    </Box>

                    {/* Price Range */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <PriceIcon sx={{ 
                        color: '#D2691E', 
                        fontSize: '18px' 
                      }} />
                      <Typography variant="body2">
                        <strong>Budżet:</strong> {profile.priceRange || 'Nie określono'}
                      </Typography>
                    </Box>

                    {/* Move-in date */}
                    {profile.moveInDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ color: '#D2691E', fontSize: '18px' }} />
                        <Typography variant="body2">
                          <strong>Od:</strong> {new Date(profile.moveInDate).toLocaleDateString('pl-PL')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Description */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2 }}
                  >
                    {profile.description 
                      ? (profile.description.length > 50 ? profile.description.substring(0, 50) + '...' : profile.description)
                      : 'Brak opisu'
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            );
          })}
        </Box>

        {/* Mobile Navigation Dots */}
        {isMobile && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            gap: 1 
          }}>
            {profiles.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === currentIndex ? '#D2691E' : '#CBD5E0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TenantProfileCarousel;